import React from 'react';

/**
 * Hairline metric grid.
 *
 * Treatment from the design reference
 * (docs/revamp-july-2026/handoff/Focal Point Site.dc.html, proof section): a
 * 1px gap over a --line background inside a --line border, 12px radius and
 * overflow hidden, so the gap reads as a hairline rule between --card cells.
 * Numbers at 40px Outfit 500 in --ink-blue, captions at 16px Public Sans in
 * --text-2.
 *
 * The hairline is produced by the parent's background showing through the gap,
 * which is why the cells carry no borders of their own. Below the md breakpoint
 * the cells stack and the same mechanism draws horizontal rules instead.
 *
 * Extracted rather than inlined: #7922's AI Adoption page uses the same
 * treatment, and a second hand-rolled copy would be a fork of a shared pattern.
 */

export interface Metric {
  /** Short figure, e.g. "7 weeks". Rendered at display size. */
  value: string;
  caption: string;
}

interface MetricGridProps {
  metrics: Metric[];
  className?: string;
}

export default function MetricGrid({ metrics, className = '' }: MetricGridProps) {
  return (
    <div
      className={`grid gap-px overflow-hidden rounded-xl border border-line bg-line md:grid-cols-3 ${className}`.trim()}
    >
      {metrics.map((metric) => (
        <div key={metric.value} className="bg-card px-8 py-[34px]">
          <p className="metric-value font-display text-[34px] font-medium leading-none tracking-[-0.035em] text-ink-blue md:text-[40px]">
            {metric.value}
          </p>
          <p className="pt-2.5 font-sans text-base leading-[1.6] text-body">{metric.caption}</p>
        </div>
      ))}
    </div>
  );
}
